import {
  RootState,
  TWalletAdapterState,
  TWalletSessionState,
} from '@/types/store'
import {walletAdapter, store} from '@/store'
import {AccessorWrapper} from '@simpli/vuex-typescript'
import {WalletPlatform} from '@/enums/WalletPlatform'
import {TWalletNetwork} from '@/model/wallets/types/WalletNetwork'
import {siteNetwork, walletNetworksInUse} from '@/helpers/NetworkModeRegistry'

export const wrapper = new AccessorWrapper<TWalletAdapterState, RootState>(
  'walletAdapter'
)

/**
 * Wallet sessions are per (implementation, network). The `n3*`/`x*` members
 * below read the session of the network in use right now — the site's, or a
 * feature's own while the user is on it. Pass `network` explicitly to reach a
 * specific session (the bridge does, so it never signs on the site's chain).
 */
export abstract class WalletAdapter {
  static $accessors = wrapper.accessors

  // Getters
  static get n3Address() {
    return WalletAdapter.$accessors.read(walletAdapter.getters.n3Address)(store)
  }

  static get xAddress() {
    return WalletAdapter.$accessors.read(walletAdapter.getters.xAddress)(store)
  }

  static get n3Uri() {
    return WalletAdapter.$accessors.read(walletAdapter.getters.n3Uri)(store)
  }

  static get xUri() {
    return WalletAdapter.$accessors.read(walletAdapter.getters.xUri)(store)
  }

  static get n3Network() {
    return WalletAdapter.$accessors.read(walletAdapter.getters.n3Network)(store)
  }

  static get xNetwork() {
    return WalletAdapter.$accessors.read(walletAdapter.getters.xNetwork)(store)
  }

  static get n3ConnectedWalletPlatform() {
    return WalletAdapter.$accessors.read(
      walletAdapter.getters.n3ConnectedWalletPlatform
    )(store)
  }

  static get xConnectedWalletPlatform() {
    return WalletAdapter.$accessors.read(
      walletAdapter.getters.xConnectedWalletPlatform
    )(store)
  }

  /** A specific session, e.g. the N3 testnet one. */
  static session(
    walletImplementation: 'n3' | 'neoX',
    network?: TWalletNetwork
  ): TWalletSessionState {
    const sessions = WalletAdapter.$accessors.read(
      walletAdapter.getters.sessions
    )(store)

    return sessions[walletImplementation][network ?? siteNetwork()]
  }

  /** Address of a specific session. */
  static addressOf(
    walletImplementation: 'n3' | 'neoX',
    network?: TWalletNetwork
  ): string | null {
    return WalletAdapter.session(walletImplementation, network).address
  }

  /** Networks the user may need a session on. */
  static networksInUse() {
    return walletNetworksInUse()
  }

  // Actions
  static initN3Wallet(network?: TWalletNetwork) {
    return WalletAdapter.$accessors.dispatch(walletAdapter.actions.init)(
      store,
      {
        walletImplementation: 'n3',
        network,
      }
    )
  }

  static initXWallet(network?: TWalletNetwork) {
    return WalletAdapter.$accessors.dispatch(walletAdapter.actions.init)(
      store,
      {
        walletImplementation: 'neoX',
        network,
      }
    )
  }

  /** Re-points the active sessions after a route change / network selection. */
  static syncActiveSessions() {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.syncActiveSessions
    )(store)
  }

  static connectN3Wallet(
    walletPlatform: WalletPlatform,
    network?: TWalletNetwork
  ) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.connect
    )(store, {walletPlatform, walletImplementation: 'n3', network})
  }

  static connectXWallet(
    walletPlatform: WalletPlatform,
    network?: TWalletNetwork
  ) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.connect
    )(store, {walletPlatform, walletImplementation: 'neoX', network})
  }

  static beginN3WalletConnectPairing(
    walletPlatform: WalletPlatform,
    network?: TWalletNetwork
  ) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.beginWalletConnectPairing
    )(store, {walletPlatform, walletImplementation: 'n3', network})
  }

  static beginXWalletConnectPairing(
    walletPlatform: WalletPlatform,
    network?: TWalletNetwork
  ) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.beginWalletConnectPairing
    )(store, {walletPlatform, walletImplementation: 'neoX', network})
  }

  static completeN3WalletConnectPairing(
    walletPlatform: WalletPlatform,
    network?: TWalletNetwork
  ) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.completeWalletConnectPairing
    )(store, {walletPlatform, walletImplementation: 'n3', network})
  }

  static completeXWalletConnectPairing(
    walletPlatform: WalletPlatform,
    network?: TWalletNetwork
  ) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.completeWalletConnectPairing
    )(store, {walletPlatform, walletImplementation: 'neoX', network})
  }

  static cancelN3WalletConnectPairing(
    walletPlatform: WalletPlatform,
    network?: TWalletNetwork
  ) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.cancelWalletConnectPairing
    )(store, {walletPlatform, walletImplementation: 'n3', network})
  }

  static cancelXWalletConnectPairing(
    walletPlatform: WalletPlatform,
    network?: TWalletNetwork
  ) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.cancelWalletConnectPairing
    )(store, {walletPlatform, walletImplementation: 'neoX', network})
  }

  static disconnectN3Wallet(network?: TWalletNetwork) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.disconnect
    )(store, {walletImplementation: 'n3', network})
  }

  static disconnectXWallet(network?: TWalletNetwork) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.disconnect
    )(store, {walletImplementation: 'neoX', network})
  }

  static canUseN3Wallet(
    walletPlatform: WalletPlatform,
    network?: TWalletNetwork
  ) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.canUseWallet
    )(store, {walletPlatform, walletImplementation: 'n3', network})
  }

  static canUseXWallet(
    walletPlatform: WalletPlatform,
    network?: TWalletNetwork
  ) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.canUseWallet
    )(store, {walletPlatform, walletImplementation: 'neoX', network})
  }

  static invokeN3Wallet(params: any, network?: TWalletNetwork) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.invoke
    )(store, {params, walletImplementation: 'n3', network})
  }

  static invokeXWallet(params: any, network?: TWalletNetwork) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.invoke
    )(store, {params, walletImplementation: 'neoX', network})
  }

  static async executeWithConnectedN3Wallet(
    actionToDoAfterConnect: () => Promise<void> | void,
    network?: TWalletNetwork
  ) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.executeWithConnectedWallet
    )(store, {actionToDoAfterConnect, walletImplementation: 'n3', network})
  }

  static async executeWithConnectedXWallet(
    actionToDoAfterConnect: () => Promise<void> | void,
    network?: TWalletNetwork
  ) {
    return WalletAdapter.$accessors.dispatch(
      walletAdapter.actions.executeWithConnectedWallet
    )(store, {actionToDoAfterConnect, walletImplementation: 'neoX', network})
  }
}
